// [Phase 58-02] Read-only operator cards: Cedar policy explorer (SCORE-03)
// and NATS stream inspector (SCORE-04). Fetches artifacts as stored from the
// fabric-readonly backend router — no re-evaluation, no second Cedar engine.
import { useState, useEffect } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core';

type CedarPolicies = {
  data?: Record<string, string>;
};

export const CedarPolicyExplorerCard = () => {
  const { entity } = useEntity();
  const agent = entity.metadata.name;
  const [data, setData] = useState<CedarPolicies | null>(null);
  const [err, setErr] = useState<string>('');

  useEffect(() => {
    let alive = true;
    fetch(`/api/catalog/cedar/policies`)
      .then(r => (r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`)))
      .then((d: CedarPolicies) => alive && setData(d))
      .catch((e: any) => alive && setErr(String(e)));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Card>
      <CardHeader title="Cedar Policy Store" subheader="Read-only (as stored)" />
      <CardContent>
        {err ? <Typography color="error">{err}</Typography> : null}
        {!data && !err ? <LinearProgress /> : null}
        {data?.data ? (
          <Typography variant="body2">
            {Object.keys(data.data).length} policy file(s) in the live
            cedar-policies ConfigMap (fabric). Policies for {agent} are the
            effective permits/forbids evaluated by the ext-authz adapter.
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

type StreamInfo = {
  name?: string;
  state?: string;
  messages?: number;
  consumers?: number;
  subjects?: string[];
};

export const NatsStreamInspectorCard = () => {
  const [streams, setStreams] = useState<StreamInfo[] | null>(null);
  const [err, setErr] = useState<string>('');

  useEffect(() => {
    let alive = true;
    fetch(`/api/catalog/nats/streams`)
      .then(r => (r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`)))
      .then((d: { streams?: StreamInfo[] }) =>
        alive ? setStreams(d.streams ?? []) : undefined,
      )
      .catch((e: any) => alive && setErr(String(e)));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Card>
      <CardHeader
        title="NATS JetStream State"
        subheader="Read-only — streams, consumers, message depth"
      />
      <CardContent>
        {err ? <Typography color="error">{err}</Typography> : null}
        {!streams && !err ? <LinearProgress /> : null}
        {streams ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Stream</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="right">Messages</TableCell>
                <TableCell align="right">Consumers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {streams.map(s => (
                <TableRow key={s.name}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.state}</TableCell>
                  <TableCell align="right">{s.messages ?? 0}</TableCell>
                  <TableCell align="right">{s.consumers ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
};
